"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 9: MMC2 / PxROM  (Mike Tyson's Punch-Out!!)
 *
 * The whole point of this board is the CHR latch. Each 4 KB PPU window holds
 * two CHR banks, and the PPU picks between them while it renders, by fetching
 * tile $FD or tile $FE:
 *
 *   PPU read $0FD8        -> window $0000 switches to its $FD bank
 *   PPU read $0FE8        -> window $0000 switches to its $FE bank
 *   PPU read $1FD8-$1FDF  -> window $1000 switches to its $FD bank
 *   PPU read $1FE8-$1FEF  -> window $1000 switches to its $FE bank
 *
 * The left window only switches on the top row of the tile (it is meant for
 * sprites), the right one switches on every row (it is meant for the
 * background, where each row is fetched separately). The latch is updated
 * after the fetch, so the switching tile itself is still drawn from the old
 * bank.
 *
 *   CPU $8000-$9FFF  8 KB switchable PRG bank
 *   CPU $A000-$FFFF  the last three 8 KB banks, hard-wired
 *
 *   $A000-$AFFF  PRG bank for $8000-$9FFF (bits 0-3)
 *   $B000-$BFFF  CHR bank for PPU $0000 when latch 0 = $FD
 *   $C000-$CFFF  CHR bank for PPU $0000 when latch 0 = $FE
 *   $D000-$DFFF  CHR bank for PPU $1000 when latch 1 = $FD
 *   $E000-$EFFF  CHR bank for PPU $1000 when latch 1 = $FE
 *   $F000-$FFFF  nametable mirroring (bit 0: 0 = vertical, 1 = horizontal)
 *
 * There is no IRQ counter on this board: an earlier version of this file
 * implemented one, and banked a 16 KB window at $8000 out of registers the
 * chip does not have. Both were wrong, and both mis-banked the reset vector
 * far enough that the game never started.
 * ------------------------------------------------------------------ */

function register(Mappers) {
  Mappers[9] = function (nes) {
    this.nes = nes;
  };

  Mappers[9].prototype = new Mappers[0]();

  // The PPU has to re-fetch the pattern tables every scanline for this board:
  // the latch changes what tile $FD/$FE names, row by row.
  Mappers[9].prototype.watchesChrLatch = true;

  Mappers[9].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // $A000 register, in 8 KB units.
    this.prgBank = 0;

    // One CHR pair per PPU window. chrReg[window][0] is the $FD bank ($B000 /
    // $D000), chrReg[window][1] the $FE bank ($C000 / $E000).
    this.chrReg = [
      [0, 0],
      [0, 0],
    ];

    // Which member of each pair the PPU is reading. 0 = $FD, 1 = $FE. The chip
    // powers up on the $FD bank of each pair.
    this.latch = [0, 0];
  };

  Mappers[9].prototype.write = function (address, value) {
    if (address < 0x8000) {
      // $6000-$7FFF PRG RAM and everything below belongs to the base board.
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    // $8000-$9FFF is not decoded by this chip, so writes there fall out of the
    // switch and go nowhere.
    switch (address & 0xf000) {
      case 0xa000:
        this.prgBank = value & 0x0f;
        this.updatePrgBanks();
        break;

      case 0xb000:
        this.chrReg[0][0] = value & 0x3f;
        this.updateChrBanks();
        break;

      case 0xc000:
        this.chrReg[0][1] = value & 0x3f;
        this.updateChrBanks();
        break;

      case 0xd000:
        this.chrReg[1][0] = value & 0x3f;
        this.updateChrBanks();
        break;

      case 0xe000:
        this.chrReg[1][1] = value & 0x3f;
        this.updateChrBanks();
        break;

      case 0xf000:
        // NESdev's naming: 0 selects the A10 arrangement, 1 the A11 one.
        this.setMirroring(
          (value & 1) !== 0
            ? this.nes.rom.HORIZONTAL_MIRRORING
            : this.nes.rom.VERTICAL_MIRRORING,
        );
        break;
    }
  };

  Mappers[9].prototype.updatePrgBanks = function () {
    var banks8k = this.nes.rom.romCount * 2;
    if (banks8k === 0) {
      return;
    }

    this.load8kRomBank(this.prgBank % banks8k, 0x8000);

    // $A000-$FFFF: the last three 8 KB banks, soldered on. The game's reset
    // vector lives here and points into one of them, which is only reachable
    // once this window is a fixed bank rather than a 16 KB switchable one.
    this.load8kRomBank(banks8k - 3, 0xa000);
    this.load8kRomBank(banks8k - 2, 0xc000);
    this.load8kRomBank(banks8k - 1, 0xe000);
  };

  Mappers[9].prototype.updateChrBanks = function () {
    this.loadVromBank(this.chrReg[0][this.latch[0]], 0x0000);
    this.loadVromBank(this.chrReg[1][this.latch[1]], 0x1000);
  };

  // Called by the PPU with the pattern-table address it is fetching. Only the
  // trigger addresses below do anything.
  //
  // The asymmetry is the point of this board: the left window answers to two
  // exact addresses, the right one to two eight byte ranges. That is what makes
  // the left table switch on the top row of a tile and the right table switch
  // on every row.
  Mappers[9].prototype.latchAccess = function (address) {
    var a = address & 0x1fff;

    if (a === 0x0fd8) {
      this.setLatch(0, 0);
    } else if (a === 0x0fe8) {
      this.setLatch(0, 1);
    } else if (a >= 0x1fd8 && a <= 0x1fdf) {
      this.setLatch(1, 0);
    } else if (a >= 0x1fe8 && a <= 0x1fef) {
      this.setLatch(1, 1);
    }
  };

  Mappers[9].prototype.setLatch = function (window, value) {
    if (this.latch[window] === value) {
      return;
    }
    this.latch[window] = value;
    this.updateChrBanks();
  };

  Mappers[9].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("MMC2: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.updatePrgBanks();
    this.updateChrBanks();
    this.loadBatteryRam();
    this.loadTrainer();
    // Mirroring comes from the header until the game writes $F000, which is how
    // the board behaves: the register only overrides the hardwired arrangement.
    this.resetIrqHandler();
  };

  Mappers[9].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgBank = this.prgBank;
    // Sliced, not aliased, so a save object kept around does not change
    // underneath the caller when the latch promotes a bank.
    s.chrReg = [this.chrReg[0].slice(), this.chrReg[1].slice()];
    s.latch = this.latch.slice();
    return s;
  };

  Mappers[9].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.prgBank = s.prgBank;
    this.chrReg = [s.chrReg[0].slice(), s.chrReg[1].slice()];
    this.latch = s.latch.slice();
    // Rebuild the PPU view from the live registers, since the latch decides
    // which of the pair is on screen.
    this.updateChrBanks();
  };
}

module.exports = { register: register };
